import type { AIToolsConfig } from '@affine/core/modules/ai-button';
import { MessageContextItemKind } from '@affine/graphql';

import type { ReasoningModeId } from '../../../modules/ai-button/services/models';
import {
  type CanonicalMessageContext,
  type CanonicalMessageContextItem,
} from '../components/ai-chat-chips/canonical-message-context';
import { AIProvider } from './ai-provider';
import { getClientInstanceId } from './client-instance';
import { type CopilotClient, Endpoint } from './copilot-client';
import { delay, toTextStream } from './event-source';

const TIMEOUT = 50000;

export type TextToTextOptions = {
  client: CopilotClient;
  sessionId: string;
  threadId: string; // Required for thread isolation
  content?: string;
  attachments?: (string | Blob | File)[];
  context?: CanonicalMessageContext;
  timeout?: number;
  stream?: boolean;
  signal?: AbortSignal;
  retry?: boolean;
  // user-message ID being retried. REQUIRED when retry === true so the
  // backend's trackStreamUsage can correlate tool-loop sub-calls under the
  // original interaction (triggerId). On non-retry calls a new user-message
  // is created via createMessage() and its id is used instead.
  messageId?: string;
  endpoint?: Endpoint;
  isRootSession?: boolean;
  postfix?: (text: string) => string;
  reasoning?: boolean;
  reasoningEffort?: ReasoningModeId;
  webSearch?: boolean;
  modelId?: string;
  toolsConfig?: AIToolsConfig;
  // E010-T015: Viewport position for Edgeless canvas
  viewportX?: number;
  viewportY?: number;
  // sl-535: User language for AI responses (ISO 639-1 code)
  language?: string;
  // sl-ddz8.5.3: Bot ID for Expert Agent chat
  botId?: string;
  // sl-uq2j: Active document ID for message context (workspace-scoped chat)
  docId?: string;
  // Workspace scope of the chat — sent with stream requests so the backend
  // can validate client/workspace binding for canvas mutations.
  workspaceId?: string;
};

export type ToImageOptions = TextToTextOptions & {
  seed?: string;
};

async function resizeImage(blob: Blob | File): Promise<Blob | null> {
  let src = '';
  try {
    src = URL.createObjectURL(blob);
    const img = new Image();
    img.src = src;
    // sl-w252: Register onerror to prevent infinite hang on non-image blobs
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });

    const canvas = document.createElement('canvas');
    // keep aspect ratio
    const scale = Math.min(1024 / img.width, 1024 / img.height);
    canvas.width = Math.floor(img.width * scale);
    canvas.height = Math.floor(img.height * scale);

    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return await new Promise(resolve =>
        canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.8)
      );
    }
  } catch (e) {
    console.error(e);
  } finally {
    if (src) URL.revokeObjectURL(src);
  }
  return null;
}

interface CreateMessageOptions {
  client: CopilotClient;
  workspaceId: string;
  sessionId: string;
  threadId: string; // Required for thread isolation
  content?: string;
  attachments?: (string | Blob | File)[];
  context?: TextToTextOptions['context'];
}

async function prepareUploadFile(
  attachment: Blob | File,
  sessionId: string
): Promise<File> {
  if (!attachment.type.startsWith('image/')) {
    return new File(
      [attachment],
      attachment instanceof File ? attachment.name : sessionId,
      {
        type: attachment.type,
      }
    );
  }

  const resized = await resizeImage(attachment);
  if (!resized) {
    throw new Error('Failed to prepare image attachment for upload');
  }

  return new File(
    [resized],
    attachment instanceof File ? attachment.name || sessionId : sessionId,
    {
      type: resized.type,
    }
  );
}

async function buildUploadPlan(
  attachments: (string | Blob | File)[] | undefined,
  sessionId: string
) {
  const stringAttachments: string[] = [];
  const blobAttachments: Array<Blob | File> = [];

  for (const attachment of attachments ?? []) {
    if (typeof attachment === 'string') {
      stringAttachments.push(attachment);
    } else {
      blobAttachments.push(attachment);
    }
  }

  const blobs = await Promise.all(
    blobAttachments.map(async attachment => {
      return prepareUploadFile(attachment, sessionId);
    })
  );

  return { stringAttachments, blobs };
}

function hasUploadRef(item: CanonicalMessageContextItem) {
  return (
    typeof item.refs.uploadIndex === 'number' ||
    typeof item.refs.thumbnailUploadIndex === 'number'
  );
}

function stripClientOnlyContextPreviews(
  context: CanonicalMessageContext | undefined
): CanonicalMessageContext | undefined {
  if (!context) return undefined;

  return {
    ...context,
    items: context.items.map(item => {
      if (
        hasUploadRef(item) &&
        (item.kind === MessageContextItemKind.IMAGE ||
          item.kind === MessageContextItemKind.PDF_FILE)
      ) {
        return {
          ...item,
          display: {
            ...item.display,
            preview: null,
          },
        };
      }
      return item;
    }),
  };
}

async function createMessage({
  client,
  workspaceId,
  sessionId,
  threadId,
  content,
  attachments,
  context,
}: CreateMessageOptions): Promise<string> {
  const uploadPlan = await buildUploadPlan(attachments, sessionId);

  const options: Parameters<CopilotClient['createMessage']>[0] = {
    workspaceId,
    sessionId,
    threadId,
    content,
    context: stripClientOnlyContextPreviews(context),
  };

  if (uploadPlan.stringAttachments.length > 0) {
    options.attachments = uploadPlan.stringAttachments;
  }
  if (uploadPlan.blobs.length > 0) {
    options.blobs = uploadPlan.blobs;
  }

  return await client.createMessage(options);
}

export function textToText({
  client,
  sessionId,
  threadId,
  content,
  attachments,
  context,
  stream,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint = Endpoint.Stream,
  postfix,
  reasoning,
  reasoningEffort,
  webSearch,
  modelId,
  toolsConfig,
  // E010-T015: Viewport position for Edgeless canvas
  viewportX,
  viewportY,
  // sl-535: User language for AI responses
  language,
  // sl-ddz8.5.3: Bot ID for Expert Agent chat
  botId,
  // sl-uq2j: Active document ID for message context
  docId,
  workspaceId,
  messageId: retryMessageId,
}: TextToTextOptions) {
  // sl-535: Get language from document.documentElement.lang if not provided
  // This is set by the i18n service when language changes
  const resolvedLanguage =
    language ||
    (typeof document !== 'undefined'
      ? document.documentElement.lang
      : undefined);

  if (retry && !retryMessageId) {
    throw new Error(
      'textToText: retry=true requires messageId of the user message being retried'
    );
  }

  let messageId: string | undefined = retry ? retryMessageId : undefined;

  if (stream) {
    return {
      [Symbol.asyncIterator]: async function* () {
        if (!retry) {
          if (!workspaceId) {
            throw new Error('createMessage requires workspaceId');
          }
          messageId = await createMessage({
            client,
            workspaceId,
            sessionId,
            threadId,
            content,
            attachments,
            context,
          });
        }
        const eventSource = client.chatTextStream(
          {
            sessionId,
            threadId,
            messageId,
            reasoning,
            reasoningEffort,
            webSearch,
            modelId,
            toolsConfig,
            // E010-T015: Pass viewport to backend
            viewportX,
            viewportY,
            // sl-535: Pass language to backend
            language: resolvedLanguage,
            // sl-ddz8.5.3: Pass bot ID to backend
            botId,
            // Closed-loop canvas mutations: bind this stream to this tab,
            // the active doc, and the workspace.
            clientInstanceId: getClientInstanceId(),
            activeDocId: docId,
            workspaceId,
          },
          endpoint
        );
        AIProvider.LAST_ACTION_SESSIONID = sessionId;

        if (signal) {
          // Closing the EventSource is not reliably propagated through every
          // proxy, so additionally tell the backend to stop the tool/AI loop.
          const abortStream = () => {
            eventSource.close();
            client.abortChatStream(sessionId).catch(() => {
              // Best effort — the backend also aborts on socket close.
            });
          };
          if (signal.aborted) {
            abortStream();
            return;
          }
          signal.addEventListener('abort', abortStream, { once: true });
        }
        if (postfix) {
          const messages: string[] = [];
          for await (const event of toTextStream(eventSource, {
            timeout,
            signal,
          })) {
            if (event.type === 'message') {
              messages.push(event.data);
            }
          }
          yield postfix(messages.join(''));
        } else {
          for await (const event of toTextStream(eventSource, {
            timeout,
            signal,
          })) {
            if (event.type === 'message') {
              yield event.data;
            } else if (event.type === 'reasoning') {
              // Yield as JSON StreamObject so the parsing pipeline handles it
              yield JSON.stringify({
                type: 'reasoning',
                textDelta: event.data,
              });
            } else if (event.type === 'tool-call-start') {
              // sl-718z: Early tool notification — name known, args still streaming
              try {
                const tcs = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'tool-call',
                  toolCallId: tcs.toolCallId || '',
                  toolName: tcs.toolName,
                  displayName: tcs.displayName,
                  args: {},
                });
              } catch {
                /* skip malformed */
              }
            } else if (event.type === 'tool-call') {
              // sl-718z: Forward tool-call SSE events as StreamObjects
              try {
                const tc = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'tool-call',
                  toolCallId: tc.id,
                  toolName: tc.name,
                  displayName: tc.displayName,
                  args:
                    typeof tc.arguments === 'string'
                      ? JSON.parse(tc.arguments)
                      : tc.arguments,
                });
              } catch {
                /* skip malformed tool-call events */
              }
            } else if (event.type === 'tool-execution-started') {
              // sl-z4tb.5: Forward execution-started for immediate UI feedback
              try {
                const tes = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'tool-execution-started',
                  toolCalls: tes.toolCalls,
                  totalCount: tes.totalCount,
                });
              } catch {
                /* skip malformed */
              }
            } else if (event.type === 'tool-result') {
              // sl-718z: Forward tool-result SSE events as StreamObjects
              try {
                const tr = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'tool-result',
                  toolCallId: tr.toolCallId,
                  toolName: tr.name,
                  displayName: tr.displayName,
                  args: tr.args ?? {},
                  result: tr.result,
                  affectedRefs: tr.affectedRefs ?? tr.result?.affectedRefs,
                });
              } catch {
                /* skip malformed tool-result events */
              }
            } else if (event.type === 'usage') {
              // sl-opil.15.3: Forward usage/credits info for managed AI calls
              try {
                const u = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'usage',
                  creditsUsed: u.creditsUsed,
                  model: u.model,
                  promptTokens: u.promptTokens,
                  completionTokens: u.completionTokens,
                  totalTokens: u.totalTokens,
                });
              } catch {
                /* skip malformed usage events */
              }
            } else if (event.type === 'routing') {
              // sl-sf9o: Forward routing telemetry for badge display
              try {
                const routing = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'routing',
                  ...routing,
                });
              } catch {
                /* skip malformed routing events */
              }
            } else if (event.type === 'meta') {
              // sl-k8wk.2.2: Forward low-balance meta-event
              try {
                const meta = JSON.parse(event.data);
                yield JSON.stringify({
                  type: 'meta',
                  ...meta,
                });
              } catch {
                /* skip malformed meta events */
              }
            }
          }
        }
      },
    };
  } else {
    return Promise.race([
      timeout
        ? delay(timeout).then(() => {
            throw new Error('Timeout');
          })
        : null,
      (async function () {
        if (!retry) {
          if (!workspaceId) {
            throw new Error('createMessage requires workspaceId');
          }
          messageId = await createMessage({
            client,
            workspaceId,
            sessionId,
            threadId,
            content,
            attachments,
            context,
          });
        }
        AIProvider.LAST_ACTION_SESSIONID = sessionId;

        return client.chatText({
          sessionId,
          threadId,
          messageId,
          reasoning,
          reasoningEffort,
          webSearch,
          modelId,
          // sl-535: Pass language to backend
          language: resolvedLanguage,
        });
      })(),
    ]);
  }
}

// Only one image is currently being processed
export function toImage({
  content,
  sessionId,
  threadId,
  attachments,
  context,
  seed,
  botId,
  signal,
  timeout = TIMEOUT,
  retry = false,
  endpoint,
  client,
  workspaceId,
  messageId: retryMessageId,
}: ToImageOptions) {
  if (retry && !retryMessageId) {
    throw new Error(
      'toImage: retry=true requires messageId of the user message being retried'
    );
  }
  let messageId: string | undefined = retry ? retryMessageId : undefined;
  return {
    [Symbol.asyncIterator]: async function* () {
      if (!retry) {
        if (!workspaceId) {
          throw new Error('createMessage requires workspaceId');
        }
        messageId = await createMessage({
          client,
          workspaceId,
          sessionId,
          threadId,
          content,
          attachments,
          context,
        });
      }
      const eventSource = client.imagesStream(
        sessionId,
        threadId,
        messageId,
        seed,
        botId,
        endpoint
      );
      AIProvider.LAST_ACTION_SESSIONID = sessionId;

      for await (const event of toTextStream(eventSource, {
        timeout,
        signal,
      })) {
        if (event.type === 'attachment') {
          yield event.data;
        }
      }
    },
  };
}
